import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Divider,
    alpha,
} from '@mui/material';
import { ExpandMore, MeetingRoom, HelpOutline } from '@mui/icons-material';
import { ILocationList } from '../../interfaces/ILocationList';

interface LocationDetailsProps {
    location: ILocationList;
    expanded?: string;
    onChange?: (panel: string) => void;
    roomIds?: string[];
    isTBD?: boolean;
    primaryColor?: string;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({
    location,
    expanded,
    onChange,
    roomIds,
    isTBD,
    primaryColor = '#493193'
}) => {
    const formatAddress = () => {
        const parts = [
            location?.streetAddress,
            location?.city,
            location?.region,
            location?.postalCode,
            location?.country
        ].filter(Boolean);
        return parts.join(', ');
    };

    if (isTBD) {
        return (
            <Box sx={{
                p: 3,
                mb: 2,
                bgcolor: alpha(primaryColor, 0.05),
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(primaryColor, 0.15)}`,
                boxShadow: `0 2px 8px ${alpha(primaryColor, 0.05)}`,
            }}>
                <HelpOutline sx={{ mr: 2, color: primaryColor, fontSize: 32 }} />
                <Typography
                    variant="body1"
                    sx={{
                        color: "text.primary",
                        fontStyle: "italic",
                        fontWeight: 500,
                        letterSpacing: 0.2,
                        '& b': {
                            color: primaryColor,
                        }
                    }}
                >
                    Location and room assignments for this day are <b>To Be Determined</b>.
                </Typography>
            </Box>
        );
    }

    if (!location) {
        return (
            <Box>
                <Typography variant="body1" color="error">
                    Location not found.
                </Typography>
            </Box>
        );
    }

    const displayRooms = roomIds
        ? location.rooms?.filter((room) => roomIds.includes(room.id ?? '')) || []
        : location.rooms || [];

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        fontWeight: 600,
                        color: primaryColor,
                    }}
                >
                    {location?.name}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {formatAddress()}
                </Typography>
            </Box>

            <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 600, 
                    mt: 3, 
                    mb: 2,
                    color: primaryColor,
                }}
            >
                Available Rooms
            </Typography>

            {displayRooms.length > 0 ? (
                displayRooms.map((room) => (
                    <Accordion
                        key={room.id}
                        expanded={expanded === room.id}
                        onChange={() => onChange?.(expanded === room.id ? '' : room.id ?? '')}
                        sx={{ 
                            mb: 1,
                            borderRadius: 2,
                            border: `1px solid ${alpha(primaryColor, 0.1)}`,
                            '&:before': { display: 'none' },
                            boxShadow: `0 2px 8px ${alpha(primaryColor, 0.05)}`,
                            '&.Mui-expanded': {
                                boxShadow: `0 4px 12px ${alpha(primaryColor, 0.1)}`,
                            }
                        }}
                    >
                        <AccordionSummary 
                            expandIcon={<ExpandMore sx={{ color: primaryColor }} />}
                            sx={{
                                '&:hover': {
                                    bgcolor: alpha(primaryColor, 0.02),
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MeetingRoom sx={{ color: primaryColor }} />
                                <Typography sx={{ fontWeight: 500 }}>{room.name}</Typography>
                                <Chip
                                    label={`Capacity: ${room.capacity}`}
                                    size="small"
                                    sx={{ 
                                        ml: 1,
                                        borderRadius: '50px',
                                        bgcolor: alpha(primaryColor, 0.08),
                                        color: primaryColor,
                                        fontWeight: 500,
                                        border: `1px solid ${alpha(primaryColor, 0.2)}`,
                                    }}
                                />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography 
                                        variant="subtitle2" 
                                        color="text.secondary" 
                                        gutterBottom
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Facilities
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {(room.facilities ?? []).length > 0 ? (
                                            (room.facilities ?? []).map((f, i) => (
                                                <Chip
                                                    key={`facility-${room.id ?? 'unknown'}-${i}`}
                                                    label={f}
                                                    size="small"
                                                    sx={{ 
                                                        borderRadius: '50px',
                                                        bgcolor: alpha(primaryColor, 0.05),
                                                        color: 'text.primary',
                                                        border: `1px solid ${alpha(primaryColor, 0.15)}`,
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                    </Stack>
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography 
                                        variant="subtitle2" 
                                        color="text.secondary" 
                                        gutterBottom
                                        sx={{ fontWeight: 600 }}
                                    >
                                        Accessibility Features
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {(room.accessibilityFeatures ?? []).length > 0 ? (
                                            (room.accessibilityFeatures ?? []).map((a, i) => (
                                                <Chip
                                                    key={`access-${room.id ?? 'unknown'}-${i}`}
                                                    label={a}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: '50px',
                                                        bgcolor: alpha(primaryColor, 0.08),
                                                        color: primaryColor,
                                                        border: `1px solid ${alpha(primaryColor, 0.2)}`,
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">-</Typography>
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                ))
            ) : (
                <Typography>No rooms available for this location.</Typography>
            )}
        </Box>
    );
};

export default LocationDetails;